/**
 * Price Formatter
 * Formats prices to display integer part (euros) in normal size
 * and decimal part (cents) in a smaller size
 */

/**
 * Parses a price string and extracts the integer and decimal parts
 * Handles formats like: €79,50, 79,50 €, €79.50, 79.50 €, etc.
 * @param {string} priceText - The price text to parse
 * @returns {{integer: string, decimal: string, currency: string, prefix: string} | null}
 */
function parsePrice(priceText) {
  if (!priceText || typeof priceText !== 'string') return null;

  // Remove HTML entities and trim
  const text = priceText.replace(/&nbsp;/g, ' ').trim();
  if (!text) return null;

  // Match price patterns: currency symbol, digits, comma/dot, digits, currency symbol
  // Handles: €79,50, 79,50 €, €79.50, 79.50 €, etc.
  const pricePattern = /([€$£]?)\s*(\d+)\s*[,\.]\s*(\d{2})\s*([€$£]?)/;
  const match = text.match(pricePattern);

  if (match) {
    const [, prefixCurrency, integer, decimal, suffixCurrency] = match;
    const currency = prefixCurrency || suffixCurrency || '';
    const prefix = prefixCurrency || '';
    
    return {
      integer,
      decimal,
      currency,
      prefix
    };
  }

  // If no decimal part found, check if it's a whole number with currency
  const wholeNumberPattern = /([€$£]?)\s*(\d+)\s*([€$£]?)/;
  const wholeMatch = text.match(wholeNumberPattern);
  
  if (wholeMatch) {
    const [, prefixCurrency, integer, suffixCurrency] = wholeMatch;
    const currency = prefixCurrency || suffixCurrency || '';
    const prefix = prefixCurrency || '';
    
    return {
      integer,
      decimal: null,
      currency,
      prefix
    };
  }

  return null;
}

/**
 * Formats a price element by wrapping integer and decimal parts in spans
 * @param {HTMLElement} element - The price element to format
 */
function formatPriceElement(element) {
  // Skip if already formatted (has price-int or price-dec class as children or itself)
  if (element.querySelector('.price-int, .price-dec') || element.classList.contains('price-formatted')) {
    return;
  }

  // Get text content, preserving structure but removing HTML tags
  const originalText = element.textContent || element.innerText;
  const parsed = parsePrice(originalText);

  if (!parsed) {
    return;
  }

  // Mark as formatted to avoid reprocessing
  element.classList.add('price-formatted');

  // Create the formatted HTML
  let formattedHTML = '';
  
  if (parsed.prefix) {
    formattedHTML += `<span class="price-currency">${parsed.prefix}</span>`;
  }
  
  formattedHTML += `<span class="price-int">${parsed.integer}</span>`;
  
  if (parsed.decimal !== null) {
    formattedHTML += `<span class="price-dec">,${parsed.decimal}</span>`;
  }
  
  if (parsed.currency && !parsed.prefix) {
    formattedHTML += `<span class="price-currency"> ${parsed.currency}</span>`;
  }

  element.innerHTML = formattedHTML;
  
  // Preserve any aria attributes or roles that might be on the element
  // The existing attributes are already preserved by innerHTML replacement
}

/**
 * Formats all price elements in the document
 */
function formatAllPrices() {
  // Select all price elements (including compare-at-price and unit-price)
  const priceSelectors = [
    '.price:not(.price-formatted)',
    '.compare-at-price:not(.price-formatted)',
    '.unit-price:not(.price-formatted)'
  ];

  priceSelectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    elements.forEach(element => {
      // Only format if the element contains actual price text (not already formatted)
      if (element.textContent && element.textContent.trim() && !element.querySelector('.price-int')) {
        formatPriceElement(element);
      }
    });
  });

  // Also format price ranges (e.g., "79,50 - 99,50")
  const priceRanges = document.querySelectorAll('.price-range:not(.price-formatted)');
  priceRanges.forEach(range => {
    const text = range.textContent;
    if (text && text.includes(' - ')) {
      const parts = text.split(' - ');
      if (parts.length === 2) {
        const leftParsed = parsePrice(parts[0]);
        const rightParsed = parsePrice(parts[1]);
        
        if (leftParsed && rightParsed) {
          range.classList.add('price-formatted');
          let formattedHTML = '';
          
          // Format left price
          if (leftParsed.prefix) {
            formattedHTML += `<span class="price-currency">${leftParsed.prefix}</span>`;
          }
          formattedHTML += `<span class="price-int">${leftParsed.integer}</span>`;
          if (leftParsed.decimal !== null) {
            formattedHTML += `<span class="price-dec">,${leftParsed.decimal}</span>`;
          }
          if (leftParsed.currency && !leftParsed.prefix) {
            formattedHTML += `<span class="price-currency"> ${leftParsed.currency}</span>`;
          }
          
          formattedHTML += ' - ';
          
          // Format right price
          if (rightParsed.prefix) {
            formattedHTML += `<span class="price-currency">${rightParsed.prefix}</span>`;
          }
          formattedHTML += `<span class="price-int">${rightParsed.integer}</span>`;
          if (rightParsed.decimal !== null) {
            formattedHTML += `<span class="price-dec">,${rightParsed.decimal}</span>`;
          }
          if (rightParsed.currency && !rightParsed.prefix) {
            formattedHTML += `<span class="price-currency"> ${rightParsed.currency}</span>`;
          }
          
          range.innerHTML = formattedHTML;
        }
      }
    }
  });
}

/**
 * Initializes the price formatter
 */
function initPriceFormatter() {
  // Format prices on page load
  formatAllPrices();

  // Debounce function to avoid excessive calls
  let debounceTimer;
  function debouncedFormat() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      formatAllPrices();
    }, 100);
  }

  // Use MutationObserver to handle dynamically loaded content
  const observer = new MutationObserver((mutations) => {
    // Only process if there are actual changes
    let shouldFormat = false;
    for (const mutation of mutations) {
      // Check if added nodes contain price elements
      if (mutation.addedNodes.length > 0) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if it's a price element or contains price elements
            if (
              node.matches?.('.price, .compare-at-price, .unit-price, .price-range') ||
              node.querySelector?.('.price, .compare-at-price, .unit-price, .price-range')
            ) {
              shouldFormat = true;
              break;
            }
          }
        }
        if (shouldFormat) break;
      }
    }
    
    if (shouldFormat) {
      debouncedFormat();
    }
  });

  // Observe the document body for changes
  if (document.body) {
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPriceFormatter);
} else {
  initPriceFormatter();
}

// Also initialize after a short delay to catch any late-loading content
setTimeout(initPriceFormatter, 100);
